import { log as debugLog } from '@/utils/logger'
import BadgeComponent from './Badge.jsx'
import PanelComponent from './Panel.jsx'
import { LabelOverlaySeries } from '@/indicators/ict_quantum/LabelOverlaySeries'
import { SegmentsSeries } from './SegmentsSeries.js'

// ─── Canon v1.0 — constantes visuales (NO configurables) ─────────────────────
// Pools = ZONAS con la familia de color del Standard: los hex EXACTOS de
// order_blocks_quantum / ict_quantum (paleta Quantum validada en producción).
//   - sell-side pool (bajo mínimos, soporte) → token BULLISH (como OB alcista)
//   - buy-side pool (sobre máximos, resistencia) → token BEARISH (como OB baj.)
// CISD → token de acento neutro (oro del Standard). CERO colores nuevos.
export const POOL_TOKENS = Object.freeze({
  buy: '#B71C1C', // buy-side pool = colorBear del Standard
  sell: '#1B5E20', // sell-side pool = colorBull del Standard
})
export const CISD_COLOR = '#B08D57' // oro de acento del Standard

// LWC 4.2.3 no permite reordenar series dentro del pane (no hay moveTo ni
// índice de inserción: las custom series se pintan siempre ENCIMA de la serie
// de velas, creada antes). El efecto "bandas DETRÁS de las velas" se consigue
// con alpha de fill inferior al de las zonas OB (0.14): las bandas LP son
// contexto, no protagonistas (canon §6 — workaround documentado en la ficha).
const POOL_FILL_ALPHA = 0.10
const POOL_SWEPT_FILL_FACTOR = 0.4 // barrido → fill al ~40% del alpha activo
const POOL_BORDER_ALPHA = 0.55
const POOL_BORDER_WIDTH = 1
const CISD_MAX_MARKERS = 2 // solo los últimos 2 eventos CISD visibles
const CISD_TRIANGLE_SIZE = 5 // tamaño mínimo del Standard

// ─── Helpers de tiempo y validación ───────────────────────────────────────────
// Null-safety estricta: el backend puede mandar time null y precios fuera de
// rango; todo item inválido se descarta ANTES de construir filas de datos, y
// las filas se ordenan por time y se deduplican (lightweight-charts lanza
// "Value is null" de forma diferida ante tiempos duplicados o no finitos).

function cleanTime(t) {
  if (t == null || t === '' || Number.isNaN(t)) return null
  const s = t > 1e10 ? Math.floor(t / 1000) : t
  if (!Number.isFinite(s) || s <= 0) return null
  return s
}

function cleanValue(v) {
  if (v == null || Number.isNaN(v) || !Number.isFinite(v)) return null
  return v
}

// ─── Datos externos (inyectados desde ChartPage / ChartPublicPage) ────────────
// Respuesta COMPLETA del endpoint dedicado /charting/liquidity-pools:
// { s, timeframe, timestamp, pools: [...], cisd_events: [...], stats: {...} }.
// El backend ya aplica caps 3+3 por lado con ranking y el ciclo de vida del
// barrido (20 barras): el frontend NO re-filtra ni re-ordena, solo proyecta.

let externalData = null
let externalVersion = 0

export function setLiquidityPoolsQuantumData(data) {
  externalData = data
  externalVersion++
}

export function getExternalData() {
  return externalData
}

export function getExternalVersion() {
  return externalVersion
}

// ─── Configuración por defecto (canon §4 — minimalista) ───────────────────────
// Toda la lógica de detección la fija el canon y la aplica el backend: aquí
// solo quedan los dos toggles visuales y el dashboard. Al subir _version a 3,
// useIndicatorConfig.resetea cualquier config v1/v2 guardada (localStorage y
// remoto) a estos defaults — las claves eliminadas desaparecen solas.

export const defaultConfig = {
  _version: 3,

  // ── Visibilidad ──────────────────────────────────────────────────────────
  showPools: true,
  showCisd: true,

  // ── Dashboard ────────────────────────────────────────────────────────────
  showDashboard: true,
  dashboardMinimized: false,
  badgePosition: { x: 8, y: 8 },
  panelSections: {},
}

// ─── detect() ─────────────────────────────────────────────────────────────────

export function detect(candles, config) {
  const t0 = typeof performance !== 'undefined' ? performance.now() : 0
  if (!externalData) {
    const t1 = typeof performance !== 'undefined' ? performance.now() : 0
    debugLog(`[liquidity_pools_quantum] detect empty: ${(t1 - t0).toFixed(2)}ms`)
    return null
  }
  const result = {
    ...externalData,
    _dataVersion: externalVersion,
  }
  const t1 = typeof performance !== 'undefined' ? performance.now() : 0
  debugLog(`[liquidity_pools_quantum] detect from external: ${(t1 - t0).toFixed(2)}ms`)
  return result
}

// ─── render() ─────────────────────────────────────────────────────────────────
// Arquitectura de series FIJAS (máx. 4): pools_sell y pools_buy (una
// SegmentsSeries por lado, cada una pinta TODAS sus bandas en un solo canvas
// pass), cisd (triángulos ▲▼, misma SegmentsSeries) y UNA LabelOverlaySeries
// para todas las etiquetas (EQH/EQL al borde derecho + texto CISD del evento
// más reciente). NUNCA una serie por pool. Los swings NO se renderizan bajo
// ninguna configuración (canon §2) — su contador vive en el badge.

export function render(chart, candleSeries, result, config, api) {
  const t0 = typeof performance !== 'undefined' ? performance.now() : 0
  if (!result) return

  const cfg = { ...defaultConfig, ...(config || {}) }

  const allCandles = candleSeries && candleSeries.data ? candleSeries.data() : []
  // Ventana de DATOS cargada — fija e independiente del viewport (mismo
  // criterio que OB/MB Quantum): la detección corre sobre ella y el viewport
  // solo clipea visualmente; una zona pintada jamás aparece/desaparece al pan.
  const firstCandleTime = allCandles.length > 0 ? cleanTime(allCandles[0].time) : null
  const lastCandleTime = allCandles.length > 0 ? cleanTime(allCandles[allCandles.length - 1].time) : null

  // Anclaje temporal de las bandas (canon §3.1):
  // - barSeconds: separación temporal entre velas (mediana de las últimas
  //   diferencias — robusta ante huecos), para expresar el límite de la
  //   proyección como timestamp (time2, documental/contrato).
  // - rightOffsetBars: el rightOffset del chart (5 en ChartRenderer) leído de
  //   las opciones del time scale — el mismo límite que las zonas OB/FVG.
  const barSeconds = (() => {
    const diffs = []
    for (let i = Math.max(1, allCandles.length - 50); i < allCandles.length; i++) {
      const a = cleanTime(allCandles[i - 1]?.time)
      const b = cleanTime(allCandles[i]?.time)
      if (a != null && b != null && b > a) diffs.push(b - a)
    }
    if (diffs.length === 0) return 3600
    diffs.sort((x, y) => x - y)
    return diffs[Math.floor(diffs.length / 2)]
  })()
  const rightOffsetBars = (() => {
    try {
      const v = chart?.timeScale?.().options?.()?.rightOffset
      return Number.isFinite(v) && v >= 0 ? v : 5
    } catch { return 5 }
  })()

  // High/low por tiempo para anclar los triángulos CISD FUERA de la vela.
  const candleByTime = new Map()
  for (const c of allCandles) {
    const t = cleanTime(c?.time)
    if (t != null) candleByTime.set(t, c)
  }

  const labelsByTime = {}
  const addLabel = (opts) => {
    const t = cleanTime(opts?.time)
    if (t == null || !Number.isFinite(opts?.price)) return
    if (!labelsByTime[t]) labelsByTime[t] = []
    labelsByTime[t].push({ ...opts, time: t })
  }

  // Bandas agrupadas por lado (una SegmentsSeries por lado) + triángulos CISD.
  const segGroups = { sell: [], buy: [], cisd: [] }

  const counts = { poolsActivos: 0, eqh: 0, eql: 0, cisd: 0, lastCisd: null }

  // ── Pools = ZONAS ──────────────────────────────────────────────────────────
  if (cfg.showPools !== false) {
    for (const pool of result.pools || []) {
      const side = String(pool?.side || '').toLowerCase()
      if (side !== 'buy' && side !== 'sell') continue
      const kind = ['eqh', 'eql', 'pool'].includes(String(pool?.kind || '').toLowerCase())
        ? String(pool.kind).toLowerCase()
        : 'pool'
      const top = cleanValue(pool.top)
      const bottom = cleanValue(pool.bottom)
      const start = cleanTime(pool.start_time)
      if (top == null || bottom == null || start == null || top === bottom) continue

      const swept = String(pool?.state || '').toLowerCase() === 'barrido'
      const sweepTime = cleanTime(pool.sweep_time)
      // Anclaje canon §3.1: activo = tramo histórico hasta la última vela
      // cerrada + proyección dashed hasta el FIN DEL RIGHTOFFSET (time2 =
      // lastCandle + rightOffsetBars × barSeconds — mismo límite que OB/FVG;
      // la x la resuelve SegmentsSeries con projBars × spacing, sin filas en
      // el futuro). Barrido: banda terminada en el sweep (el backend lo
      // elimina a las 20 barras; aquí solo se pinta atenuado mientras llegue).
      const end = swept ? (sweepTime ?? lastCandleTime) : lastCandleTime
      if (end == null || end < start) continue
      // Reconciliación con la ventana cargada: intersecta o se descarta;
      // borde izquierdo clipeado a la primera vela.
      if (firstCandleTime != null && end < firstCandleTime) continue
      if (lastCandleTime != null && start > lastCandleTime) continue
      const paintStart = firstCandleTime != null ? Math.max(start, firstCandleTime) : start

      const isEq = kind === 'eqh' || kind === 'eql'
      const color = POOL_TOKENS[side]
      const fillAlpha = swept ? POOL_FILL_ALPHA * POOL_SWEPT_FILL_FACTOR : POOL_FILL_ALPHA
      // Textura del borde: EQH/EQL sólido, pool simple dashed, barrido dotted
      // (el estado manda sobre el kind, como HTF manda en OB/MB).
      const borderStyle = swept ? 'dotted' : isEq ? 'solid' : 'dashed'

      segGroups[side].push({
        kind: 'band',
        time0: paintStart,
        time1: end,
        time2: !swept && lastCandleTime != null
          ? lastCandleTime + rightOffsetBars * barSeconds
          : null,
        upper: Math.max(top, bottom),
        lower: Math.min(top, bottom),
        color,
        fillAlpha,
        borderAlpha: POOL_BORDER_ALPHA,
        borderWidth: POOL_BORDER_WIDTH,
        borderStyle,
        project: !swept,
        projectStyle: 'dashed',
        projBars: rightOffsetBars,
      })

      // Etiqueta pequeña al borde derecho SOLO para EQH/EQL activos (canon
      // §3): texto plano del Standard, anclada a la última vela cerrada con
      // offset a la derecha (zona de rightOffset, sin tapar velas). Pool
      // simple y barrido: sin etiqueta.
      if (isEq && !swept && lastCandleTime != null) {
        addLabel({
          time: lastCandleTime,
          price: kind === 'eqh' ? Math.max(top, bottom) : Math.min(top, bottom),
          text: kind.toUpperCase(),
          color,
          textColor: color,
          anchor: kind === 'eqh' ? 'above' : 'below',
          offset: 4,
          textAlign: 'left',
          xOffset: 6,
          priority: 5,
          plaqueStyle: 'plain',
        })
      }

      if (!swept) {
        counts.poolsActivos += 1
        if (kind === 'eqh') counts.eqh += 1
        else if (kind === 'eql') counts.eql += 1
      }
    }
  }

  // ── CISD: triángulo fuera de la vela, solo los últimos 2 eventos ──────────
  const cisdEvents = (result.cisd_events || [])
    .map((ev) => ({
      direction: String(ev?.direction || '').toLowerCase() === 'bull' ? 'bull'
        : String(ev?.direction || '').toLowerCase() === 'bear' ? 'bear' : null,
      level: cleanValue(ev?.level),
      time: cleanTime(ev?.time),
      afterSweep: ev?.after_sweep === true,
    }))
    .filter((ev) => ev.direction && ev.time != null)
    .sort((a, b) => a.time - b.time)

  // Último CISD del dataset — lo muestra el badge (dirección + confluencia).
  const lastCisdEvent = cisdEvents.length > 0 ? cisdEvents[cisdEvents.length - 1] : null
  counts.lastCisd = lastCisdEvent
    ? { direction: lastCisdEvent.direction, time: lastCisdEvent.time, afterSweep: lastCisdEvent.afterSweep }
    : null

  if (cfg.showCisd !== false) {
    // Visibles SOLO los últimos 2 (canon §5); etiqueta de texto solo en el
    // más reciente.
    const visible = cisdEvents.slice(-CISD_MAX_MARKERS)
    for (let i = 0; i < visible.length; i++) {
      const ev = visible[i]
      if (firstCandleTime != null && ev.time < firstCandleTime) continue
      if (lastCandleTime != null && ev.time > lastCandleTime) continue
      const candle = candleByTime.get(ev.time)
      const high = cleanValue(candle?.high)
      const low = cleanValue(candle?.low)
      // ▼ sobre el high (bear) / ▲ bajo el low (bull) — FUERA de la vela;
      // fallback al nivel del evento si la vela no está en la ventana.
      const anchorPrice = ev.direction === 'bear'
        ? (high ?? ev.level)
        : (low ?? ev.level)
      if (anchorPrice == null) continue

      segGroups.cisd.push({
        kind: 'triangle',
        time: ev.time,
        price: anchorPrice,
        direction: ev.direction === 'bear' ? 'down' : 'up',
        color: CISD_COLOR,
        size: CISD_TRIANGLE_SIZE,
      })
      counts.cisd += 1

      const isMostRecent = i === visible.length - 1
      if (isMostRecent) {
        addLabel({
          time: ev.time,
          price: anchorPrice,
          text: 'CISD',
          color: CISD_COLOR,
          textColor: CISD_COLOR,
          anchor: ev.direction === 'bear' ? 'above' : 'below',
          offset: 14,
          priority: 8,
          plaqueStyle: 'plain',
        })
      }
    }
  }

  // ── Series fijas: una SegmentsSeries por grupo con datos ──────────────────
  // OJO: no pasar `title` — con title no vacío LWC dibuja la etiqueta de la
  // serie en el panel (SeriesPriceAxisView: showSymbolLabel = title !== ''),
  // ensuciando el borde derecho del chart.
  let seriesCreated = 0
  let totalBands = 0
  let totalTriangles = 0
  const SERIES_GROUPS = ['sell', 'buy', 'cisd']
  for (const group of SERIES_GROUPS) {
    const segments = segGroups[group]
    if (segments.length === 0) continue
    // Filas: una por tiempo extremo, válidas, ordenadas y deduplicadas, cada
    // una con un `price` FINITO (contrato LWC: priceValueBuilder nunca puede
    // producir undefined/±Infinity — ver SegmentsSeries.js). El precio de la
    // fila es el de un item que toca ese tiempo; solo alimenta los plot
    // values internos de LWC, no el dibujo (eso lo hacen los items).
    const priceByTime = new Map()
    for (const s of segments) {
      const p = s.kind === 'band' ? s.upper : s.price
      if (!Number.isFinite(p)) continue
      if (s.kind === 'triangle') {
        if (!priceByTime.has(s.time)) priceByTime.set(s.time, p)
        continue
      }
      if (!priceByTime.has(s.time0)) priceByTime.set(s.time0, p)
      if (!priceByTime.has(s.time1)) priceByTime.set(s.time1, p)
    }
    const rows = [...priceByTime.keys()]
      .sort((a, b) => a - b)
      .map((t) => ({ time: t, _t: t, price: priceByTime.get(t) }))
    if (rows.length === 0) continue
    const paneView = new SegmentsSeries()
    paneView.setSegments(segments)
    // Base temporal para la pps canónica (barSpacing de LWC ÷ barSeconds):
    // inmune a la recreación por tick en vivo (canon §3.1, ronda 3.2).
    paneView.setTimeBase({ barSeconds })
    // Ancla de precio: la escala viva de las velas (en ventanas sin velas
    // visibles, el convertidor LWC del custom series diverge del eje —
    // ronda 3.3). Null-safety: sin candleSeries devuelve null y el item se
    // salta (§4.2), nunca dibuja con una escala dudosa.
    paneView.setPriceFn((p) => (candleSeries && typeof candleSeries.priceToCoordinate === 'function'
      ? candleSeries.priceToCoordinate(p)
      : null))
    const s = api.addCustomSeries(paneView, {
      color: 'rgba(0,0,0,0)',
      lineWidth: 0,
      lastValueVisible: false,
      priceLineVisible: false,
      crosshairMarkerVisible: false,
      autoscaleInfoProvider: () => null,
    })
    if (s) {
      s.setData(rows)
      seriesCreated += 1
      totalBands += segments.filter((seg) => seg.kind === 'band').length
      totalTriangles += segments.filter((seg) => seg.kind === 'triangle').length
    }
  }

  // Contadores de lo REALMENTE pintado — única fuente del badge (el objeto
  // result viaja por referencia hasta indResults, como en OB/MB Quantum).
  // `stats.swings` es estado interno del backend: no se pinta ni se muestra.
  result._paintedCounts = counts

  // ── Etiquetas en canvas: UNA LabelOverlaySeries para todas ─────────────────
  if (Object.keys(labelsByTime).length > 0) {
    const labelSeries = api.addCustomSeries(new LabelOverlaySeries(), {
      color: 'transparent',
      lineWidth: 0,
      lastValueVisible: false,
      priceLineVisible: false,
      crosshairMarkerVisible: false,
      autoscaleInfoProvider: () => null,
      priceScaleId: 'right',
      plaqueStyle: 'plain',
      font: '10px -apple-system, BlinkMacSystemFont, "Trebuchet MS", Roboto, Ubuntu, sans-serif',
    })
    if (labelSeries) {
      const labelData = Object.keys(labelsByTime)
        .map((t) => ({ time: Number(t), labels: labelsByTime[t] }))
        .sort((a, b) => a.time - b.time)
      labelSeries.setData(labelData)
      seriesCreated += 1
    }
  }

  const t1 = typeof performance !== 'undefined' ? performance.now() : 0
  debugLog(`[liquidity_pools_quantum] render done: ${(t1 - t0).toFixed(2)}ms (series=${seriesCreated}, bands=${totalBands}, triangles=${totalTriangles})`)

  // Exponer datos de render para verificación programática externa (Playwright):
  // seriesCreated debe mantenerse ≤6 con cualquier volumen de datos.
  if (typeof window !== 'undefined') {
    window.__liquidityPoolsQuantumRender = {
      counts,
      seriesCreated,
      bands: totalBands,
      triangles: totalTriangles,
      timestamp: Date.now(),
    }
  }
}

export default {
  id: 'liquidity_pools_quantum',
  name: 'Liquidity Pools Quantum',
  version: '2.0.0',
  // Nada de este render depende del viewport: las primitivas son nativas del
  // chart y se desplazan con el pan (mismo opt-out que OB/MB Quantum).
  renderOnPan: false,
  defaultConfig,
  detect,
  render,
  BadgeComponent,
  PanelComponent,
}
